import React, { useMemo } from 'react';

import {
  ContributionStatusFilter,
  ContributionFilters as Filters,
  STATUS_FILTER_OPTIONS,
} from '~support-at-home/lib/contributionFilters';

import { MultiSelect, SelectLazy } from '~components/form-fields/inputs';
import PeriodFilter from '~support-at-home/components/config/contributions/PeriodFilter';

import { getAll } from '~main/actions/clients';

import { selectLookupClients } from '~main/selectors/clients/clients';

interface ComponentProps {
  filters: Filters;
  categories: Array<{ id: string; name: string }>;
  fundingSources: Array<{ id: number; name: string }>;
  onChange: (changes: Partial<Filters>) => void;
}

// Each control is named for assistive technology by a visually hidden label (placeholders stand in for
// visible labels so the whole bar stays on one line).
const FilterLabel = ({ htmlFor, children }: { htmlFor: string; children: string }) => (
  <label htmlFor={htmlFor} className="visually-hidden">
    {children}
  </label>
);

const ContributionFilters: React.FC<ComponentProps> = ({ filters, categories, fundingSources, onChange }) => {
  const categoryOptions = useMemo(() => categories.map(({ id, name }) => ({ id, name })), [categories]);
  const fundingOptions = useMemo(() => fundingSources.map(({ id, name }) => ({ id, name })), [fundingSources]);

  return (
    <div className="sah-contribution-filters" data-cy="sah-client-contributions-filters">
      <div className="sah-filter">
        <FilterLabel htmlFor="sah-filter-client">Filter by client</FilterLabel>
        <SelectLazy
          type="select-lazy"
          id="sah-filter-client"
          name="sah-filter-client"
          value={filters.client_id}
          placeholder="Client"
          selectorOptions={selectLookupClients}
          selectorActions={getAll}
          isMulti
          wideMenu
          summariseSelection={{ singular: 'client', plural: 'clients', ariaLabel: 'Filter by client' }}
          onChange={(_name: string, value: number[]) => onChange({ client_id: value })}
        />
      </div>
      <div className="sah-filter">
        <FilterLabel htmlFor="sah-filter-category-input">Filter by category</FilterLabel>
        <MultiSelect
          type="multi-lookup"
          id="sah-filter-category"
          name="sah-filter-category"
          value={filters.category_id}
          placeholder="Category"
          options={categoryOptions}
          wideMenu
          summariseSelection={{ singular: 'category', plural: 'categories', ariaLabel: 'Filter by category' }}
          setFieldValue={(_id: string, values: string[]) => onChange({ category_id: values })}
        />
      </div>
      <div className="sah-filter sah-filter-long-summary">
        <FilterLabel htmlFor="sah-filter-funding-input">Filter by funding source</FilterLabel>
        <MultiSelect
          type="multi-lookup"
          id="sah-filter-funding"
          name="sah-filter-funding"
          value={filters.funding_source_id}
          placeholder="Funding Source"
          options={fundingOptions}
          wideMenu
          summariseSelection={{
            singular: 'funding source',
            plural: 'funding sources',
            ariaLabel: 'Filter by funding source',
          }}
          setFieldValue={(_id: string, values: number[]) => onChange({ funding_source_id: values })}
        />
      </div>
      <div className="sah-filter sah-filter-period">
        <PeriodFilter from={filters.period_from} to={filters.period_to} onChange={(period) => onChange(period)} />
      </div>
      <div className="sah-filter">
        <FilterLabel htmlFor="sah-filter-status-input">Filter by status</FilterLabel>
        <MultiSelect
          type="multi-lookup"
          id="sah-filter-status"
          name="sah-filter-status"
          value={filters.status}
          placeholder="Any status"
          options={STATUS_FILTER_OPTIONS}
          wideMenu
          summariseSelection={{ singular: 'status', plural: 'statuses', ariaLabel: 'Filter by status' }}
          setFieldValue={(_id: string, values: ContributionStatusFilter[]) => onChange({ status: values })}
        />
      </div>
    </div>
  );
};

export default ContributionFilters;
